import { cn } from '@/utils/cn';
import { ChevronDown, ChevronUp, Clock } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

interface TimePickerProps {
  hour: number;
  minute: number;
  onHourChange: (hour: number) => void;
  onMinuteChange: (minute: number) => void;
  className?: string;
  label?: string;
  description?: string;
}

export const TimePicker: React.FC<TimePickerProps> = ({
  hour,
  minute,
  onHourChange,
  onMinuteChange,
  className,
  label,
  description,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeField, setActiveField] = useState<'hour' | 'minute'>('hour');
  const [hourInput, setHourInput] = useState(hour.toString().padStart(2, '0'));
  const [minuteInput, setMinuteInput] = useState(
    minute.toString().padStart(2, '0')
  );
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setActiveField('hour');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Sync input values with props
  useEffect(() => {
    setHourInput(hour.toString().padStart(2, '0'));
  }, [hour]);

  useEffect(() => {
    setMinuteInput(minute.toString().padStart(2, '0'));
  }, [minute]);

  const formatTime = (h: number, m: number) => {
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  const handleHourChange = (newHour: number) => {
    onHourChange(newHour);
    setHourInput(newHour.toString().padStart(2, '0'));
  };

  const handleMinuteChange = (newMinute: number) => {
    onMinuteChange(newMinute);
    setMinuteInput(newMinute.toString().padStart(2, '0'));
  };

  const handleHourInputChange = (value: string) => {
    setHourInput(value);
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 23) {
      onHourChange(numValue);
    }
  };

  const handleMinuteInputChange = (value: string) => {
    setMinuteInput(value);
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 59) {
      onMinuteChange(numValue);
    }
  };

  const handleHourInputBlur = () => {
    const numValue = parseInt(hourInput);
    if (isNaN(numValue) || numValue < 0 || numValue > 23) {
      setHourInput(hour.toString().padStart(2, '0'));
    }
  };

  const handleMinuteInputBlur = () => {
    const numValue = parseInt(minuteInput);
    if (isNaN(numValue) || numValue < 0 || numValue > 59) {
      setMinuteInput(minute.toString().padStart(2, '0'));
    }
  };

  const incrementHour = () => {
    const newHour = (hour + 1) % 24;
    handleHourChange(newHour);
  };

  const decrementHour = () => {
    const newHour = hour === 0 ? 23 : hour - 1;
    handleHourChange(newHour);
  };

  const incrementMinute = () => {
    const newMinute = (minute + 1) % 60;
    handleMinuteChange(newMinute);
  };

  const decrementMinute = () => {
    const newMinute = minute === 0 ? 59 : minute - 1;
    handleMinuteChange(newMinute);
  };

  const setCurrentTime = () => {
    const now = new Date();
    handleHourChange(now.getHours());
    handleMinuteChange(now.getMinutes());
  };

  const clearTime = () => {
    handleHourChange(0);
    handleMinuteChange(0);
  };

  return (
    <div className={cn('space-y-3', className)} ref={containerRef}>
      {label && (
        <div>
          <label className="mb-2 block text-xs font-medium text-neutral-700">
            {label}
          </label>
          {description && (
            <p className="text-xs text-neutral-500">{description}</p>
          )}
        </div>
      )}

      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            'flex w-full items-center justify-between rounded-lg border border-neutral-300 bg-white px-4 py-3 text-left text-sm transition-colors',
            'focus:border-primary-500 focus:ring-primary-100 hover:border-neutral-400 focus:ring-2',
            isOpen && 'border-primary-500 ring-primary-100 ring-2'
          )}
        >
          <div className="flex items-center gap-3">
            <Clock className="h-4 w-4 text-neutral-500" />
            <span className="font-medium text-neutral-900">
              {formatTime(hour, minute)}
            </span>
          </div>
          {isOpen ? (
            <ChevronUp className="h-4 w-4 text-neutral-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-neutral-500" />
          )}
        </button>

        {isOpen && (
          <div className="absolute top-full right-0 left-0 z-50 mt-1 rounded-lg border border-neutral-200 bg-white p-4 shadow-lg">
            <div className="text-center">
              <h6 className="mb-4 text-sm font-medium text-neutral-900">
                Entrer l'heure
              </h6>

              {/* Time display with arrows */}
              <div className="mb-6 flex items-center justify-center gap-2">
                {/* Hour */}
                <div className="flex flex-col items-center">
                  <button
                    type="button"
                    onClick={incrementHour}
                    className="mb-1 rounded p-1 transition-colors hover:bg-neutral-100"
                  >
                    <ChevronUp className="h-4 w-4 text-neutral-600" />
                  </button>

                  <input
                    type="text"
                    value={hourInput}
                    onChange={e => handleHourInputChange(e.target.value)}
                    onBlur={handleHourInputBlur}
                    onFocus={() => setActiveField('hour')}
                    className={cn(
                      'h-12 w-16 rounded-lg border-2 text-center text-xl font-semibold transition-colors focus:outline-none',
                      activeField === 'hour'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-neutral-300 text-neutral-700'
                    )}
                    maxLength={2}
                    placeholder="00"
                  />

                  <button
                    type="button"
                    onClick={decrementHour}
                    className="mt-1 rounded p-1 transition-colors hover:bg-neutral-100"
                  >
                    <ChevronDown className="h-4 w-4 text-neutral-600" />
                  </button>
                </div>

                {/* Separator */}
                <div className="text-2xl font-bold text-neutral-400">:</div>

                {/* Minute */}
                <div className="flex flex-col items-center">
                  <button
                    type="button"
                    onClick={incrementMinute}
                    className="mb-1 rounded p-1 transition-colors hover:bg-neutral-100"
                  >
                    <ChevronUp className="h-4 w-4 text-neutral-600" />
                  </button>

                  <input
                    type="text"
                    value={minuteInput}
                    onChange={e => handleMinuteInputChange(e.target.value)}
                    onBlur={handleMinuteInputBlur}
                    onFocus={() => setActiveField('minute')}
                    className={cn(
                      'h-12 w-16 rounded-lg border-2 text-center text-xl font-semibold transition-colors focus:outline-none',
                      activeField === 'minute'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-neutral-300 text-neutral-700'
                    )}
                    maxLength={2}
                    placeholder="00"
                  />

                  <button
                    type="button"
                    onClick={decrementMinute}
                    className="mt-1 rounded p-1 transition-colors hover:bg-neutral-100"
                  >
                    <ChevronDown className="h-4 w-4 text-neutral-600" />
                  </button>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={setCurrentTime}
                  className="flex-1 rounded-md bg-neutral-100 px-3 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-200"
                >
                  Maintenant
                </button>
                <button
                  type="button"
                  onClick={clearTime}
                  className="text-primary-600 flex-1 rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm font-medium transition-colors hover:bg-neutral-50"
                >
                  Effacer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
